import React, { FC, ComponentProps } from 'react';

import { ACCEPT_LINK_ACTION_ID } from '@/shared/constants/notifications';
import { Button } from '@/shared/components/Button/Button';
import { useOpenInfoBlockFullscreenNotification } from '@/shared/components/InfoBlockFullscreenNotification/hooks/useOpenInfoBlockFullscreenNotification';

import styles from './ButtonWithDescription.module.css';

type ButtonWithDescriptionAlignment = 'default' | 'centered';

interface ButtonWithDescriptionProps extends ComponentProps<typeof Button> {
    text: string;
    alignment?: ButtonWithDescriptionAlignment;
    description?: string;
}

export const ButtonWithDescription: FC<ButtonWithDescriptionProps> = ({
    as,
    text,
    description,
    variant,
    alignment = 'default',
    size,
    theme,
    href,
    onClick,
}) => (
    <div className={styles[`root_${alignment}`]}>
        <Button
            as={as}
            className={styles.button}
            size={size}
            onClick={onClick}
            variant={variant}
            theme={theme}
            href={href}
        >
            {text}
        </Button>

        {description && <div className={styles.description}>{description}</div>}
    </div>
);

type FullscreenInfoButtonProps = ButtonWithDescriptionProps & {
    type: string;
};

export const FullscreenInfoButton: FC<FullscreenInfoButtonProps> = ({
    type,
    alignment = 'default',
    ...baseProps
}) => {
    const openFullscreen = useOpenInfoBlockFullscreenNotification({
        type,
    });

    return (
        <ButtonWithDescription
            as="button"
            alignment={alignment}
            {...baseProps}
            onClick={openFullscreen}
        />
    );
};

type ExternalLinkButtonProps = ButtonWithDescriptionProps & {
    actionId: string;
    url: string;
};

export const ExternalLinkButton: FC<ExternalLinkButtonProps> = ({
    actionId,
    url,
    text,
    variant,
    description,
    theme,
    alignment = 'default',
    ...baseProps
}) => {
    if (actionId !== ACCEPT_LINK_ACTION_ID || !url) {
        return null;
    }

    const handleClick = () => {
        window.open(url, '_blank');
    };

    return (
        <ButtonWithDescription
            as="a"
            text={text}
            variant={variant}
            description={description}
            theme={theme}
            alignment={alignment}
            {...baseProps}
            onClick={handleClick}
        />
    );
};
